<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { NButton, NSpace, NSpin, NText } from 'naive-ui';
import { usePromptLibraryStore } from '@/stores/prompt_library';
import { IconCheck } from '@/components/design/icons';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaSwitch from '@/components/design/base/GaiaSwitch.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaInputNumber from '@/components/design/base/inputs/GaiaInputNumber.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import {
  buildInitialFormValues,
  validateAndNormalizeFormValues,
  type HitlChoiceSubmission,
  type NormalizedChoice,
  type NormalizedInterruptRequest,
} from '@/utils/humanInTheLoop/normalizeInterrupt';

const props = withDefaults(
  defineProps<{
    request: NormalizedInterruptRequest;
    disabled?: boolean;
    /** Whether this list currently owns the digit/arrow/Enter keyboard shortcuts. */
    active?: boolean;
    finalized?: boolean;
    finalizedLabel?: string | null;
  }>(),
  {
    disabled: false,
    active: false,
    finalized: false,
    finalizedLabel: null,
  }
);

const emit = defineEmits<{ confirm: [submission: HitlChoiceSubmission] }>();

const focusedIndex = ref(0);
const expandedChoiceKey = ref<string | null>(null);
// `confirm` mode only: the option the user has picked but not yet sent. It drives
// both the row highlight and which option's `data` the preview pane shows.
const selectedChoiceKey = ref<string | null>(null);
const chatMessage = ref('');
const editedArgsText = ref('');
const formValues = ref<Record<string, unknown>>({});
const validationError = ref<string | null>(null);
const libraryCategory = ref('');
const libraryCollection = ref('');

const promptLibraryStore = usePromptLibraryStore();
const {
  metaCategories,
  isLoading: isPromptLibraryLoading,
  initialized: isPromptLibraryInitialized,
  isInitializing: isPromptLibraryInitializing,
} = storeToRefs(promptLibraryStore);

const libraryCategoryOptions = computed(() =>
  metaCategories.value.map(category => ({ label: category.name, value: category.name }))
);
const libraryCollectionOptions = computed(() => {
  const category = metaCategories.value.find(item => item.name === libraryCategory.value);
  return category ? category.categories.map(item => ({ label: item.name, value: item.name })) : [];
});

onMounted(() => {
  if (!isPromptLibraryInitialized.value && !isPromptLibraryInitializing.value) {
    void promptLibraryStore.initialize();
  }
  window.addEventListener('keydown', onKeydown);
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown);
});

const expandedChoice = computed<NormalizedChoice | null>(
  () => props.request.choices.find(choice => choice.key === expandedChoiceKey.value) ?? null
);

const isConfirmMode = computed(() => props.request.selectionMode === 'confirm');
const selectedChoice = computed<NormalizedChoice | null>(
  () => props.request.choices.find(choice => choice.key === selectedChoiceKey.value) ?? null
);
// The preview column only earns its space when the backend actually authored
// per-option `data` — otherwise the options keep the full width.
const hasPreviewData = computed(() =>
  props.request.choices.some(choice => Boolean(choice.data?.trim()))
);
const showPreview = computed(() => isConfirmMode.value && hasPreviewData.value);

function resetExpandState(): void {
  focusedIndex.value = 0;
  expandedChoiceKey.value = null;
  chatMessage.value = '';
  editedArgsText.value = '';
  formValues.value = {};
  validationError.value = null;
  libraryCategory.value = '';
  libraryCollection.value = '';
}

watch(
  () => props.request.requestKey,
  () => {
    resetExpandState();
    selectedChoiceKey.value = null;
  }
);

function selectChoice(choice: NormalizedChoice, index: number): void {
  if (props.disabled) return;
  focusedIndex.value = index;

  if (choice.kind === 'plain') {
    // In `confirm` mode the click only picks — the answer is sent by the Confirm
    // button, so the user can read the option's preview before committing.
    if (isConfirmMode.value) {
      selectedChoiceKey.value = choice.key;
      return;
    }
    emit('confirm', { choiceKey: choice.key, message: null, input: null, editedArgs: null });
    return;
  }

  expandedChoiceKey.value = choice.key;
  validationError.value = null;

  if (choice.kind === 'edit-json') {
    const argsDetail = props.request.details.find(detail => detail.label === 'Arguments');
    editedArgsText.value = argsDetail?.value.kind === 'text' ? argsDetail.value.text : '{}';
  } else if (choice.kind === 'schema-form' && choice.schema) {
    formValues.value = buildInitialFormValues(choice.schema);
  }
}

function confirmSelection(): void {
  if (props.disabled || !selectedChoiceKey.value) return;
  emit('confirm', {
    choiceKey: selectedChoiceKey.value,
    message: null,
    input: null,
    editedArgs: null,
  });
}

// Backing out of an expanded editor keeps any pending selection — it wasn't sent,
// and re-picking it just to answer would be busywork.
function cancelExpand(): void {
  resetExpandState();
  const selectedIndex = props.request.choices.findIndex(
    choice => choice.key === selectedChoiceKey.value
  );
  if (selectedIndex !== -1) focusedIndex.value = selectedIndex;
}

function confirmExpand(): void {
  const choice = expandedChoice.value;
  if (!choice || props.disabled) return;

  if (choice.kind === 'chat') {
    const message = chatMessage.value.trim();
    if (!message) {
      validationError.value = 'Enter a message first.';
      return;
    }
    emit('confirm', {
      choiceKey: choice.isChatOption ? null : choice.key,
      message,
      input: null,
      editedArgs: null,
    });
    return;
  }

  if (choice.kind === 'edit-json') {
    let parsed: unknown;
    try {
      parsed = JSON.parse(editedArgsText.value || '{}');
    } catch {
      validationError.value = 'Edited arguments must be valid JSON.';
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      validationError.value = 'Edited arguments must be a JSON object.';
      return;
    }
    emit('confirm', {
      choiceKey: choice.key,
      message: null,
      input: null,
      editedArgs: parsed as Record<string, unknown>,
    });
    return;
  }

  if (choice.kind === 'schema-form' && choice.schema) {
    const { isValid, normalizedInput, errors } = validateAndNormalizeFormValues(
      choice.schema,
      formValues.value
    );
    if (!isValid) {
      validationError.value =
        Object.values(errors)[0] ?? 'Complete required fields before submitting.';
      return;
    }
    emit('confirm', {
      choiceKey: choice.key,
      message: null,
      input: normalizedInput,
      editedArgs: null,
    });
  }
}

function setFieldValue(fieldName: string, value: unknown): void {
  formValues.value = { ...formValues.value, [fieldName]: value };
}

function setLibraryCategory(value: string | null): void {
  libraryCategory.value = value ?? '';
  libraryCollection.value = '';
  setFieldValue('collection_name', '');
}

function setLibraryCollection(value: string | null): void {
  libraryCollection.value = value ?? '';
  const name =
    libraryCategory.value && libraryCollection.value
      ? `${libraryCategory.value} - ${libraryCollection.value}`
      : '';
  setFieldValue('collection_name', name);
}

function isTypingTarget(el: Element | null): boolean {
  if (!el) return false;
  return (
    el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || (el as HTMLElement).isContentEditable
  );
}

function onKeydown(event: KeyboardEvent): void {
  if (!props.active || props.disabled || props.finalized) return;
  if (isTypingTarget(document.activeElement)) return;
  if (expandedChoiceKey.value) return;

  if (/^[1-9]$/.test(event.key)) {
    const index = Number(event.key) - 1;
    if (index < props.request.choices.length) {
      event.preventDefault();
      selectChoice(props.request.choices[index], index);
    }
    return;
  }
  switch (event.key) {
    case 'ArrowDown': {
      event.preventDefault();
      moveFocus(Math.min(focusedIndex.value + 1, props.request.choices.length - 1));

      break;
    }
    case 'ArrowUp': {
      event.preventDefault();
      moveFocus(Math.max(focusedIndex.value - 1, 0));

      break;
    }
    case 'Enter': {
      event.preventDefault();
      // In `confirm` mode Enter sends what's already picked; only when nothing is
      // picked yet does it fall through to picking the focused row.
      if (isConfirmMode.value && selectedChoiceKey.value) {
        confirmSelection();
        break;
      }
      const choice = props.request.choices.at(focusedIndex.value);
      if (choice) selectChoice(choice, focusedIndex.value);

      break;
    }
    // No default
  }
}

// Arrowing through the list in `confirm` mode carries the selection with it, so the
// preview pane tracks the keyboard the same way it tracks clicks.
function moveFocus(index: number): void {
  focusedIndex.value = index;
  if (!isConfirmMode.value) return;
  const choice = props.request.choices.at(index);
  if (choice?.kind === 'plain') selectedChoiceKey.value = choice.key;
}
</script>

<template>
  <NSpace vertical :size="6" class="hitl-choice-list">
    <NSpace v-if="finalized" align="center" :size="8" class="hitl-choice-finalized">
      <n-icon :component="IconCheck" :size="16" aria-hidden="true" />
      <span>{{ finalizedLabel }}</span>
    </NSpace>

    <template v-else-if="!expandedChoice">
      <div class="hitl-choice-columns" :class="{ 'hitl-choice-columns--split': showPreview }">
        <div class="hitl-choice-options">
          <button
            v-for="(choice, index) in request.choices"
            :key="choice.key"
            type="button"
            class="hitl-choice-row"
            :class="{
              'hitl-choice-row--focused': index === focusedIndex,
              'hitl-choice-row--selected': choice.key === selectedChoiceKey,
            }"
            :disabled="disabled"
            :aria-pressed="isConfirmMode ? choice.key === selectedChoiceKey : undefined"
            @click="selectChoice(choice, index)"
            @mouseenter="focusedIndex = index"
            @focus="focusedIndex = index"
          >
            <span class="hitl-choice-index">{{ index + 1 }}</span>
            <span class="hitl-choice-label">{{ choice.label }}</span>
          </button>
        </div>

        <aside v-if="showPreview" class="hitl-choice-preview">
          <template v-if="selectedChoice">
            <div class="hitl-choice-preview-title">{{ selectedChoice.label }}</div>
            <div v-if="selectedChoice.data" class="hitl-choice-preview-body">
              {{ selectedChoice.data }}
            </div>
            <div v-else class="hitl-choice-preview-empty">No preview for this option.</div>
          </template>
          <div v-else class="hitl-choice-preview-empty">Select an option to preview it.</div>
        </aside>
      </div>

      <n-space v-if="isConfirmMode" justify="end">
        <n-button
          size="small"
          type="primary"
          :disabled="disabled || !selectedChoiceKey"
          @click="confirmSelection"
        >
          Confirm
        </n-button>
      </n-space>
    </template>

    <NSpace v-else vertical :size="10">
      <button type="button" class="hitl-choice-back" :disabled="disabled" @click="cancelExpand">
        ‹ Back
      </button>

      <gaia-textarea
        v-if="expandedChoice.kind === 'chat'"
        v-model:value="chatMessage"
        :autosize="{ minRows: 2, maxRows: 6 }"
        :placeholder="
          expandedChoice.isChatOption ? 'Say what you\'d like instead…' : 'Optional reason'
        "
        :disabled="disabled"
      />

      <gaia-textarea
        v-else-if="expandedChoice.kind === 'edit-json'"
        v-model:value="editedArgsText"
        :autosize="{ minRows: 3, maxRows: 8 }"
        placeholder="Edited tool args as JSON object"
        :disabled="disabled"
      />

      <template v-else-if="expandedChoice.kind === 'schema-form' && expandedChoice.schema">
        <NSpace v-for="field in expandedChoice.schema.fields" :key="field.name" vertical :size="6">
          <label class="hitl-form-label">
            {{ field.label }}
            <span v-if="field.required" class="hitl-form-required">*</span>
          </label>

          <NSpin v-if="field.kind === 'library-collection'" :show="isPromptLibraryLoading">
            <div class="hitl-library-selectors">
              <gaia-select
                :value="libraryCategory || null"
                placeholder="Select a category"
                filterable
                :disabled="disabled"
                :options="libraryCategoryOptions"
                @update:value="setLibraryCategory"
              />
              <gaia-select
                :value="libraryCollection || null"
                placeholder="Select a collection"
                filterable
                :disabled="disabled || !libraryCategory || isPromptLibraryLoading"
                :options="libraryCollectionOptions"
                @update:value="setLibraryCollection"
              />
            </div>
          </NSpin>

          <gaia-select
            v-else-if="field.kind === 'select'"
            :value="(formValues[field.name] as string | null | undefined) ?? null"
            :options="field.enumOptions ?? []"
            :disabled="disabled"
            @update:value="value => setFieldValue(field.name, value)"
          />

          <gaia-switch
            v-else-if="field.kind === 'switch'"
            :value="Boolean(formValues[field.name])"
            :disabled="disabled"
            @update:value="value => setFieldValue(field.name, value)"
          />

          <gaia-input-number
            v-else-if="field.kind === 'number'"
            :value="(formValues[field.name] as number | null | undefined) ?? null"
            :disabled="disabled"
            :show-button="false"
            @update:value="value => setFieldValue(field.name, value)"
          />

          <gaia-textarea
            v-else-if="field.kind === 'json-array' || field.kind === 'json-object'"
            :value="String(formValues[field.name] ?? '')"
            :autosize="{ minRows: 2, maxRows: 6 }"
            :disabled="disabled"
            :placeholder="field.kind === 'json-array' ? '[...]' : '{...}'"
            @update:value="value => setFieldValue(field.name, value)"
          />

          <gaia-input
            v-else
            :value="String(formValues[field.name] ?? '')"
            :type="field.kind === 'textarea' ? 'textarea' : 'text'"
            :autosize="field.kind === 'textarea' ? { minRows: 2, maxRows: 4 } : undefined"
            :disabled="disabled"
            :placeholder="field.description || `Enter ${field.label}`"
            @update:value="value => setFieldValue(field.name, value)"
          />

          <div v-if="field.description" class="hitl-form-help">{{ field.description }}</div>
        </NSpace>
      </template>

      <NText v-if="validationError" type="error" class="hitl-choice-error">
        {{ validationError }}
      </NText>

      <NSpace justify="end">
        <NButton size="small" type="primary" :disabled="disabled" @click="confirmExpand">
          {{ expandedChoice.kind === 'chat' ? 'Send' : 'Confirm' }}
        </NButton>
      </NSpace>
    </NSpace>
  </NSpace>
</template>

<style scoped>
.hitl-choice-list {
  width: 100%;
  /* Query target for the options/preview split below. It sits on the wrapper, not on
     the grid itself — a container query only matches descendants of its container. */
  container-type: inline-size;
}

/* The chat lives in resizable panes (review project) and the Word task pane, so the
   split has to react to the card's own width — a viewport media query would lie.
   Single column is the default, so browsers without @container just stack. */
.hitl-choice-columns {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 10px;
  width: 100%;
}

@container (min-width: 560px) {
  .hitl-choice-columns--split {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
}

.hitl-choice-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.hitl-choice-preview {
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
}

html.dark .hitl-choice-preview {
  border-color: rgba(148, 163, 184, 0.24);
  background: rgba(30, 41, 59, 0.5);
}

.hitl-choice-preview-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 6px;
  overflow-wrap: anywhere;
}

html.dark .hitl-choice-preview-title {
  color: #94a3b8;
}

/* Assistant-authored prose, shown verbatim — keep its line breaks. */
.hitl-choice-preview-body {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.hitl-choice-preview-empty {
  opacity: 0.65;
}

.hitl-choice-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: transparent;
  text-align: left;
  font-size: inherit;
  cursor: pointer;
  transition:
    background-color 0.12s ease,
    border-color 0.12s ease;
}

html.dark .hitl-choice-row {
  border-color: rgba(148, 163, 184, 0.24);
}

.hitl-choice-row:not(:disabled):hover,
.hitl-choice-row--focused {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

html.dark .hitl-choice-row:not(:disabled):hover,
html.dark .hitl-choice-row--focused {
  background: rgba(148, 163, 184, 0.12);
  border-color: rgba(148, 163, 184, 0.4);
}

.hitl-choice-row--selected,
.hitl-choice-row--selected:not(:disabled):hover {
  border-color: #2563eb;
  background: #eff6ff;
}

html.dark .hitl-choice-row--selected,
html.dark .hitl-choice-row--selected:not(:disabled):hover {
  border-color: #60a5fa;
  background: rgba(37, 99, 235, 0.18);
}

.hitl-choice-row--selected .hitl-choice-index {
  color: #2563eb;
}

html.dark .hitl-choice-row--selected .hitl-choice-index {
  color: #60a5fa;
}

.hitl-choice-row:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.hitl-choice-index {
  flex: none;
  min-width: 18px;
  font-weight: 700;
  color: #64748b;
}

html.dark .hitl-choice-index {
  color: #94a3b8;
}

.hitl-choice-label {
  min-width: 0;
  overflow-wrap: anywhere;
}

.hitl-choice-finalized {
  font-size: inherit;
  color: #16a34a;
}

html.dark .hitl-choice-finalized {
  color: #4ade80;
}

.hitl-choice-back {
  align-self: flex-start;
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

html.dark .hitl-choice-back {
  color: #94a3b8;
}

.hitl-choice-back:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.hitl-choice-error {
  display: block;
}

.hitl-form-label {
  font-size: inherit;
  font-weight: 600;
}

.hitl-form-required {
  color: #ef4444;
}

.hitl-form-help {
  font-size: inherit;
  opacity: 0.72;
}

.hitl-library-selectors {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
</style>
